<script setup lang="ts">
/**
 * Los servicios que corren en una temporada.
 *
 * Es el otro lado de la misma relación: lo mismo que se hace en el paso
 * Availability de un servicio, pero entrando por la temporada. Sirve para el
 * caso que motivó todo el cambio — armar "Verano 2027-2028" cuando los
 * servicios ya existen: se abre la temporada y se le van sumando, en vez de
 * entrar a cada servicio a repetir las mismas dos fechas.
 *
 * **Pantalla y no panel**, con las mismas columnas que el listado de Services:
 * son los mismos datos y merecen el mismo ancho. El panel lateral queda para
 * cargar el precio y los días de un servicio puntual.
 *
 * El switch **guarda al instante**, como el de Active del listado de offerings:
 * vincular tres servicios no puede costar tres paneles. Un vínculo recién
 * creado no tiene días, así que todavía no genera nada en el calendario.
 */
import { computed, onMounted, ref } from 'vue'
import { getDiveCenter } from '@/api/dive-centers'
import { listOfferings } from '@/api/offerings'
import { createSchedule, deleteSchedule, listSchedules } from '@/api/schedules'
import { listSeasons } from '@/api/seasons'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import ScheduleDrawer from '@/components/ScheduleDrawer.vue'
import { apiMessage, notifyError, notifySuccess, notifyWarning } from '@/composables/useSnackbar'
import { usePreferences } from '@/composables/usePreferences'
import { scheduleGroupDaysLabel, seasonDateRange } from '@/data/offeringHelpers'
import { categoryForOffering, labelForCategory, labelForOffering } from '@/data/offerings'
import { useAuthStore } from '@/stores/auth'
import type {
  DiveCenter,
  Offering,
  OfferingSchedule,
  ScheduleImpact,
  Season,
  SeasonStatus,
} from '@/types'

definePage({
  meta: {
    requiresAuth: true,
    centerConfig: true,

    // Sin esto el grupo Offerings se cierra al entrar: ningún hijo del menú
    // matchea esta ruta.
    navActiveLink: 'dive-centers-id-offerings-seasons',
  },
})

const route = useRoute('dive-centers-id-offerings-seasons-season-id')
const router = useRouter()
const id = String(route.params.id)
const seasonId = String(route.params.seasonId)

const auth = useAuthStore()
const { formatCurrency } = usePreferences()

const season = ref<Season | null>(null)
const offerings = ref<Offering[]>([])
const schedules = ref<OfferingSchedule[]>([])
const diveCenter = ref<DiveCenter | null>(null)
const loading = ref(true)
const error = ref('')
const togglingId = ref<string | null>(null)

const drawer = ref(false)
const editingOffering = ref<Offering | null>(null)

const currency = computed(() => auth.provider?.currency ?? 'EUR')

const STATUS_CHIP: Record<SeasonStatus, { label: string; color: string }> = {
  upcoming: { label: 'Upcoming', color: 'info' },
  running: { label: 'Running', color: 'success' },
  finished: { label: 'Finished', color: 'secondary' },
}

const headers = [
  { title: 'Service', key: 'title' },
  { title: 'Category', key: 'category', sortable: false },
  { title: 'Price', key: 'price', sortable: false, align: 'end' as const },
  { title: 'Days & times', key: 'days', sortable: false },
  { title: 'Runs in this season', key: 'linked', sortable: false, width: 150 },
  { title: '', key: 'actions', sortable: false, width: 56, align: 'end' as const },
]

/** Solo los servicios: un alquiler no tiene disponibilidad. */
const services = computed(() => offerings.value.filter(o => o.kind === 'service'))

const scheduleOf = (offeringId: string) => schedules.value.find(s => s.offeringId === offeringId)

const isLinked = (offeringId: string) => !!scheduleOf(offeringId)

const categoryLabel = (o: Offering) =>
  o.taxonomyCode ? labelForCategory(categoryForOffering(o.taxonomyCode) ?? '') : ''

/** El precio del vínculo; si no tiene, el de la ficha, que es el que aplica. */
const priceOf = (o: Offering) => {
  const schedule = scheduleOf(o.id)

  if (schedule?.price?.amount != null)
    return { amount: schedule.price.amount, note: schedule.price.note, inherited: false }

  if (o.price?.amount != null)
    return { amount: o.price.amount, note: o.price.note, inherited: true }

  return null
}

const daysOf = (o: Offering) => {
  const schedule = scheduleOf(o.id)
  if (!schedule)
    return []

  return schedule.scheduleGroups
    .filter(g => g.days.length)
    .map(g => ({
      days: scheduleGroupDaysLabel(g.days),
      times: g.slots.filter(s => s.time).map(s => s.time).join(', '),
    }))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [seasonRes, offeringRes, scheduleRes, dcRes] = await Promise.all([
      listSeasons(id),
      listOfferings(id),
      listSchedules(id, { seasonId }),
      getDiveCenter(id),
    ])

    season.value = seasonRes.seasons.find(s => s.id === seasonId) ?? null
    offerings.value = offeringRes.offerings
    schedules.value = scheduleRes.schedules
    diveCenter.value = dcRes.diveCenter
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load this season.'
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/**
 * Lo que le pasó al calendario, dicho.
 *
 * `detached` es el número que importa: son salidas futuras CON gente anotada
 * que la regla nueva ya no produce. No se borran solas — quedan marcadas y las
 * resuelve el provider — pero si no se dicen, aparecen en ámbar en el
 * calendario sin ninguna explicación de por qué.
 */
function reportImpact(impact: ScheduleImpact | null, fallback: string) {
  if (impact?.detached) {
    notifyWarning(
      `${impact.detached} activit${impact.detached === 1 ? 'y' : 'ies'} with divers booked `
      + 'no longer match the schedule. Review them in Plans & Bookings.',
    )

    return
  }

  notifySuccess(fallback)
}

async function onToggle(o: Offering) {
  const existing = scheduleOf(o.id)

  togglingId.value = o.id
  try {
    if (existing) {
      const res = await deleteSchedule(id, existing.id)

      schedules.value = schedules.value.filter(s => s.id !== existing.id)
      reportImpact(res.impact, `"${o.title}" no longer runs in this season.`)
    }
    else {
      // Sin días todavía: el vínculo existe pero no genera nada. El panel se
      // abre a continuación para cargarlos.
      const res = await createSchedule(id, {
        offeringId: o.id,
        seasonId,
        price: o.price?.amount != null ? { amount: o.price.amount } : null,
        scheduleGroups: [],
        excludedDates: [],
      })

      schedules.value = [...schedules.value, res.schedule]
      openSchedule(o)
    }
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not update this service.'))
  }
  finally {
    togglingId.value = null
  }
}

function openSchedule(o: Offering) {
  editingOffering.value = o
  drawer.value = true
}

/**
 * El aviso va PRIMERO y el refresco después.
 *
 * Guardar dispara la regeneración del calendario de esa offering —doce meses—,
 * así que la respuesta tarda un par de segundos. Con el orden inverso, el panel
 * se cerraba y la confirmación aparecía tarde, cuando el usuario ya había
 * dejado de mirar.
 *
 * Y se recargan **solo los horarios**: es lo único que cambió. Volver a pedir
 * temporada, offerings y dive center eran tres viajes para redibujar una fila.
 */
async function onSaved(impact: ScheduleImpact | null) {
  reportImpact(impact, 'Schedule saved.')

  try {
    schedules.value = (await listSchedules(id, { seasonId })).schedules
  }
  catch {
    // Best-effort: lo guardado ya está; la próxima carga lo trae.
  }
}

const backToSeasons = () =>
  router.push({ name: 'dive-centers-id-offerings-seasons', params: { id } })
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-3 mb-6">
      <VBtn
        icon="ri-arrow-left-line"
        variant="text"
        size="small"
        color="default"
        title="Back to seasons"
        @click="backToSeasons"
      />
      <div>
        <h5 class="text-h5 mb-0">
          {{ season?.name ?? 'Season' }}
        </h5>
        <span
          v-if="season"
          class="text-body-2 text-medium-emphasis"
        >
          {{ seasonDateRange(season.from, season.to) }}
        </span>
      </div>
      <VChip
        v-if="season"
        :color="STATUS_CHIP[season.status].color"
        size="small"
        label
      >
        {{ STATUS_CHIP[season.status].label }}
      </VChip>
    </div>

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard>
      <VDataTable
        :items="services"
        item-value="id"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap rounded-0"
      >
        <template #item.title="{ item }">
          <div class="text-body-1">
            {{ item.title }}
          </div>
          <div
            v-if="item.taxonomyCode"
            class="text-caption text-medium-emphasis"
          >
            {{ labelForOffering(item.taxonomyCode) }}
          </div>
        </template>

        <template #item.category="{ item }">
          <span class="text-body-2">{{ categoryLabel(item) || '—' }}</span>
        </template>

        <template #item.price="{ item }">
          <template v-if="priceOf(item)">
            <div
              class="text-body-1 font-weight-bold"
              :class="priceOf(item)!.inherited ? 'text-medium-emphasis' : 'text-primary'"
            >
              {{ formatCurrency(priceOf(item)!.amount, currency) }}
            </div>
            <!-- Decir cuándo el precio es el de la ficha evita la lectura de
                 que esta temporada tiene un precio propio que nadie cargó. -->
            <div class="text-caption text-medium-emphasis">
              {{ priceOf(item)!.inherited ? 'inherited' : priceOf(item)!.note || 'this season' }}
            </div>
          </template>
          <span
            v-else
            class="text-caption text-disabled"
          >No price</span>
        </template>

        <template #item.days="{ item }">
          <div v-if="daysOf(item).length">
            <div
              v-for="(g, gi) in daysOf(item)"
              :key="gi"
              class="text-body-2"
            >
              <span class="font-weight-medium">{{ g.days }}</span>
              <span
                v-if="g.times"
                class="text-medium-emphasis"
              > · {{ g.times }}</span>
            </div>
          </div>
          <span
            v-else-if="isLinked(item.id)"
            class="text-caption text-warning"
          >No days yet</span>
          <span
            v-else
            class="text-caption text-disabled"
          >—</span>
        </template>

        <template #item.linked="{ item }">
          <VSwitch
            :model-value="isLinked(item.id)"
            color="primary"
            density="compact"
            hide-details
            :loading="togglingId === item.id"
            @update:model-value="onToggle(item)"
          />
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="isLinked(item.id)"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="default"
            title="Price and days"
            @click="openSchedule(item)"
          />
        </template>

        <template #no-data>
          <div class="py-8 text-center">
            <p class="text-body-1 mb-1">
              No services yet
            </p>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Add one from Offerings › Services and it will show up here.
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <ScheduleDrawer
      v-model="drawer"
      :dive-center-id="id"
      :season="season"
      :offering="editingOffering"
      :schedule="editingOffering ? scheduleOf(editingOffering.id) ?? null : null"
      :closures="diveCenter?.closures"
      :currency="currency"
      :base-price="editingOffering?.price?.amount ?? null"
      @saved="onSaved"
    />
  </div>
</template>
